import { useEffect, useState } from 'react';
import { useRouter } from 'next/router';
import { queryObjectToString } from '@helpers/queryObjectToString';
import { isEmptyString } from '@helpers/utils';

function isValidTab<T extends string>(
  value: string | null,
  validTabs: T[],
): value is T {
  if (!value) return false;

  return validTabs.includes(value as T);
}

export function useTabsWithQueryParams<T extends string>(
  defaultTab: T,
  validTabs: T[],
): {
  activePageTab: T;
  actions: {
    buildTabParam: (tab: T) => string;
  };
} {
  const { replace, query, events, pathname } = useRouter();
  const getInitialActivePageTab = () => {
    if (query.tab) {
      const decodedTab = decodeURIComponent(query.tab as string) as T;

      if (isValidTab(decodedTab, validTabs)) {
        return decodedTab;
      }
    }

    return defaultTab;
  };
  const initialActivePageTab: T = getInitialActivePageTab();

  const [activePageTab, setActivePageTab] = useState<T>(initialActivePageTab);

  const stringifiedQueryObject = queryObjectToString(query);

  const buildTabParam = (tab: T) => {
    const encodedTab = encodeURIComponent(tab as string);

    return !isEmptyString(stringifiedQueryObject)
      ? `?${stringifiedQueryObject}&tab=${encodedTab}`
      : `?tab=${encodedTab}`;
  };

  useEffect(() => {
    // Check if a valid tab is set
    // If there is no tab set, or an invalid tab is set, redirect to default tab
    if (!query.tab && !new URL(window.location.href).searchParams.get('tab')) {
      replace(
        {
          pathname: pathname,
          query: { ...query, tab: defaultTab as string },
        },
        undefined,
        { shallow: true },
      );
    } else {
      const decodedTab = decodeURIComponent(query.tab as string) as T;

      if (!isValidTab(decodedTab, validTabs)) {
        replace(
          {
            pathname: pathname,
            query: { ...query, tab: defaultTab },
          },
          undefined,
          { shallow: true },
        );
      }

      if (isValidTab(decodedTab, validTabs)) {
        setActivePageTab(decodedTab);
      }
    }

    // omit `router.replace` to prevent unnecessary re-renders
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [events, defaultTab, validTabs, activePageTab, query, pathname]);

  return { activePageTab, actions: { buildTabParam } };
}
